import React, { useState, useEffect } from 'react';
import { X, Share, Plus } from 'lucide-react';
import {
  shouldShowIOSInstallBanner,
  dismissIOSInstallBanner,
} from '../utils/iosInstallContext';

const font = "'Inria Serif', serif";

/**
 * IOSInstallBanner
 * Shown to iPhone/iPad users on any iOS browser (Safari, Chrome-iOS,
 * Firefox-iOS, Edge-iOS, …) who haven't installed the PWA yet.
 * The copy is Safari-centric ("In Safari, tap Share → Add to Home
 * Screen") so it teaches the install path even when the user is
 * currently on Chrome-iOS — reopening the same URL in Safari and
 * following the instructions Just Works.
 *
 * Behaviour:
 *   • Renders on any iOS browser except in-app WebViews (Gmail,
 *     Outlook, Instagram, … get the full-screen escape sheet
 *     instead, via InAppBrowserBanner).
 *   • Never renders once the PWA is already installed (standalone).
 *   • Dismissible; stays hidden for 24h after a dismiss and then
 *     reappears until the user actually installs the PWA.
 */
export function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldShowIOSInstallBanner()) return;
    // Slight delay so it doesn't pop up before the page paints.
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    dismissIOSInstallBanner();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="ios-install-banner"
      style={{
        position: 'fixed',
        bottom: 88, // above the bottom nav
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 32px)',
        maxWidth: 360,
        zIndex: 9000,
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        borderRadius: 18,
        padding: '14px 16px',
        boxShadow: 'var(--shadow-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          Add Allure to your Home Screen
        </p>
        <button
          data-testid="ios-install-banner-dismiss"
          onClick={dismiss}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-3)' }}
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>

      {/* Instructions — Safari-anchored so this card works from any iOS browser */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Share size={16} color="var(--accent-purple)" strokeWidth={2} />
          </span>
          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: 0, lineHeight: 1.35 }}>
            In <strong style={{ color: 'var(--text)' }}>Safari</strong>, tap the <strong style={{ color: 'var(--text)' }}>Share</strong> icon
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Plus size={16} color="var(--accent-purple)" strokeWidth={2} />
          </span>
          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: 0, lineHeight: 1.35 }}>
            Then tap <strong style={{ color: 'var(--text)' }}>Add to Home Screen</strong>
          </p>
        </div>
      </div>

      <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-3)', margin: 0, textAlign: 'center' }}>
        No App Store needed — Allure runs directly on your iPhone.
      </p>
    </div>
  );
}

export default IOSInstallBanner;
